import { useEffect, useState, useCallback } from 'react'
import type { DownloadState, PublicTransfer } from './types'
import { getTransferInfo, requestDownload, downloadEncryptedFile, ApiError } from './api'
import { parseKeyFromFragment, decryptAge, downloadFile } from './crypto'
import { Header } from './components/Header'
import { LoadingState } from './components/LoadingState'
import { ErrorState } from './components/ErrorState'
import { PasswordForm } from './components/PasswordForm'
import { TransferInfo } from './components/TransferInfo'
import { DownloadProgress } from './components/DownloadProgress'
import { DecryptingState } from './components/DecryptingState'
import { CompleteState } from './components/CompleteState'

function App() {
  const [state, setState] = useState<DownloadState>({ status: 'loading' })
  const [decryptionKey, setDecryptionKey] = useState<Uint8Array | null>(null)

  // Extract transfer ID from URL path
  const getTransferId = useCallback(() => {
    const path = window.location.pathname
    // Expected format: /s/{transferId} or /{transferId}
    const match = path.match(/\/(?:s\/)?([a-zA-Z0-9_-]+)/)
    return match ? match[1] : null
  }, [])

  // Initial load
  useEffect(() => {
    const init = async () => {
      const transferId = getTransferId()
      
      if (!transferId) {
        setState({ status: 'error', message: 'Invalid download link' })
        return
      }

      // Parse decryption key from URL fragment
      const key = parseKeyFromFragment()
      if (!key) {
        setState({ status: 'error', message: 'Missing decryption key in URL' })
        return
      }
      setDecryptionKey(key)

      try {
        const transfer = await getTransferInfo(transferId)
        
        // Check if expired
        if (transfer.expires_at && new Date(transfer.expires_at) < new Date()) {
          setState({ status: 'expired' })
          return
        }

        // Check if password required
        if (transfer.has_password) {
          setState({ status: 'password_required', transfer })
          return
        }

        setState({ status: 'ready', transfer })
      } catch (error) {
        if (error instanceof ApiError) {
          if (error.code === 'NOT_FOUND') {
            setState({ status: 'not_found' })
            return
          }
          if (error.code === 'EXPIRED') {
            setState({ status: 'expired' })
            return
          }
        }
        setState({ 
          status: 'error', 
          message: error instanceof Error ? error.message : 'Failed to load transfer' 
        })
      }
    }

    init()
  }, [getTransferId])

  // Handle download
  const handleDownload = useCallback(async (transfer: PublicTransfer, passwordHash?: string) => {
    if (!decryptionKey) {
      setState({ status: 'error', message: 'Missing decryption key' })
      return
    }

    try {
      // Request download URL
      const { download_url } = await requestDownload(transfer.id, passwordHash)

      // Download encrypted file with progress
      setState({ status: 'downloading', transfer, progress: 0 })
      
      const encryptedData = await downloadEncryptedFile(download_url, (progress) => {
        setState({ status: 'downloading', transfer, progress })
      })

      // Decrypt
      setState({ status: 'decrypting', transfer })
      
      try {
        const decryptedData = await decryptAge(encryptedData, decryptionKey)
        
        // Get filename from transfer
        const filename = transfer.files[0]?.name || 'download'
        
        // Trigger browser download
        downloadFile(decryptedData, filename)
        
        setState({ status: 'complete', transfer })
      } catch (decryptError) {
        // For MVP, if decryption fails (likely due to ChaCha20 not being implemented),
        // we'll fall back to downloading the encrypted file directly
        console.warn('Decryption not available, downloading encrypted file:', decryptError)
        
        const filename = `${transfer.files[0]?.name || 'download'}.age`
        downloadFile(encryptedData, filename)
        
        setState({ 
          status: 'error', 
          message: 'Browser decryption not yet available. Downloaded encrypted file - use `age -d` to decrypt locally.' 
        })
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'INVALID_PASSWORD') {
          setState({ status: 'password_required', transfer })
          return
        }
        if (error.code === 'LIMIT_REACHED') {
          setState({ status: 'error', message: 'Download limit reached for this file' })
          return
        }
      }
      setState({ 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Download failed' 
      })
    }
  }, [decryptionKey])

  // Handle password submit
  const handlePasswordSubmit = useCallback(async (password: string) => {
    if (state.status !== 'password_required') return

    // Hash password (simple SHA-256 for MVP)
    const encoder = new TextEncoder()
    const data = encoder.encode(password)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

    handleDownload(state.transfer, passwordHash)
  }, [state, handleDownload])

  return (
    <div className="app">
      <Header />
      
      <main className="container" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="card animate-fade-in" style={{ width: '100%' }}>
          {state.status === 'loading' && <LoadingState />}
          
          {state.status === 'error' && <ErrorState message={state.message} />}
          
          {state.status === 'not_found' && (
            <ErrorState 
              message="This file doesn't exist or has been deleted"
              title="File Not Found"
            />
          )}
          
          {state.status === 'expired' && (
            <ErrorState 
              message="This download link has expired"
              title="Link Expired"
            />
          )}
          
          {state.status === 'password_required' && (
            <PasswordForm 
              transfer={state.transfer}
              onSubmit={handlePasswordSubmit}
            />
          )}
          
          {state.status === 'ready' && (
            <TransferInfo 
              transfer={state.transfer}
              onDownload={() => handleDownload(state.transfer)}
            />
          )}
          
          {state.status === 'downloading' && (
            <DownloadProgress 
              transfer={state.transfer}
              progress={state.progress}
            />
          )}
          
          {state.status === 'decrypting' && (
            <DecryptingState transfer={state.transfer} />
          )}
          
          {state.status === 'complete' && (
            <CompleteState transfer={state.transfer} />
          )}
        </div>
      </main>

      <footer style={{ 
        padding: 'var(--space-lg)', 
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontSize: '13px'
      }}>
        <p>
          Powered by <a href="https://carbon.to" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Carbon</a>
          {' '}&middot;{' '}
          End-to-end encrypted file sharing
        </p>
        <p style={{ marginTop: 'var(--space-xs)', color: 'var(--text-dim)', fontSize: '12px' }}>
          Decryption happens entirely in your browser. We never see your files.
        </p>
      </footer>
    </div>
  )
}

export default App
